// Turning "actual size" into an actual size.
//
// The home page has always advertised "what each life stage looks like at actual size",
// and the component behind it drew three ticks at 28/44/72 px — a 1:1.6:2.6 ratio, when
// the sourced measurements give roughly 1:2.5:7. It was neither actual size nor the
// right proportion.
//
// Real size needs to know how big a CSS pixel is on this particular screen, which the
// browser will not tell you. So we ask: hold a coin or a card against the display and
// resize the outline until it matches. One tap, no permissions, works offline, and the
// answer is good enough to tell a poppy seed from a sesame seed — which is the entire
// question someone is asking.

export const CALIBRATION_KEY = "lsm_px_per_mm_v1";

/**
 * CSS reference pixels are defined as 1/96 inch, so this is what the spec implies.
 * It is only ever a starting point — real devices vary, which is why we let people
 * correct it.
 */
export const DEFAULT_PX_PER_MM = 96 / 25.4;

export interface ReferenceObject {
  id: string;
  label: string;
  /** The dimension the outline represents, in millimetres. */
  mm: number;
  /** Rendered as a circle (coins) or a rounded rectangle (cards). */
  shape: "circle" | "card";
  /** Height in mm, for cards. */
  heightMm?: number;
}

/**
 * Objects almost everyone has within reach. Dimensions are the published mint and
 * ISO/IEC 7810 ID-1 specifications, which are fixed by law and standard respectively.
 */
export const REFERENCE_OBJECTS: readonly ReferenceObject[] = [
  { id: "quarter", label: "US quarter", mm: 24.26, shape: "circle" },
  { id: "penny", label: "US penny", mm: 19.05, shape: "circle" },
  { id: "dime", label: "US dime", mm: 17.91, shape: "circle" },
  { id: "card", label: "Any bank card", mm: 85.6, heightMm: 53.98, shape: "card" },
];

/** Keep the slider inside sane bounds — roughly half to triple the nominal density. */
export const MIN_PX_PER_MM = 1.5;
export const MAX_PX_PER_MM = 12;

export function clampPxPerMm(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_PX_PER_MM;
  return Math.min(MAX_PX_PER_MM, Math.max(MIN_PX_PER_MM, value));
}

export function loadPxPerMm(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CALIBRATION_KEY);
    if (!raw) return null;
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? clampPxPerMm(value) : null;
  } catch {
    return null;
  }
}

export function savePxPerMm(value: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CALIBRATION_KEY, String(clampPxPerMm(value)));
  } catch {
    // Private mode. The session still works, it just will not be remembered.
  }
}

export function getReference(id: string): ReferenceObject | undefined {
  return REFERENCE_OBJECTS.find((r) => r.id === id);
}
